import type React from 'react';

export const ROW_CLICK_IGNORE_SELECTOR =
    '[data-row-click-ignore], button, a, input, select, textarea, [role="button"], [role="link"], [contenteditable="true"]';

export type RowNavigation = {
    readonly href: string;
    readonly navigate: () => void;
};

type NavigableElementProps = {
    readonly onClick: (event: React.MouseEvent) => void;
    readonly onAuxClick: (event: React.MouseEvent) => void;
};

/**
 * Open a URL in a background tab without switching focus.
 */
export function openInBackgroundTab(href: string): void {
    // `noopener` also stops the new tab inheriting a copy of this tab's
    // sessionStorage, which would otherwise land it on whatever chat this tab
    // was on. Matches every other window.open call in the app.
    window.open(href, '_blank', 'noopener,noreferrer');
    window.focus();
}

/**
 * Returns true when a mouse event carries a modifier key (Ctrl or Cmd)
 * that signals "open in new tab" intent.
 */
export function isNewTabClick(event: React.MouseEvent | MouseEvent): boolean {
    return event.metaKey || event.ctrlKey;
}

export function shouldIgnoreNavigationEvent(
    event: React.MouseEvent | MouseEvent,
): boolean {
    const target = event.target;
    if (!(target instanceof Element)) {
        return false;
    }
    const ignoredElement = target.closest(ROW_CLICK_IGNORE_SELECTOR);
    return !!ignoredElement && ignoredElement !== event.currentTarget;
}

/**
 * Handle a click on a navigable element. Normal clicks run the in-app
 * navigation callback, while Ctrl/Cmd-clicks keep link-like new-tab behavior.
 */
export function handleNavigableElementClick(
    event: React.MouseEvent | MouseEvent,
    navigation: RowNavigation,
): void {
    if (shouldIgnoreNavigationEvent(event)) {
        return;
    }
    if (isNewTabClick(event)) {
        event.preventDefault();
        openInBackgroundTab(navigation.href);
        return;
    }
    navigation.navigate();
}

/**
 * `onAuxClick` handler for middle-click (button === 1) on navigable rows.
 * Opens the href in a background tab. Attach to elements alongside onClick.
 */
export function handleNavigableElementAuxClick(
    event: React.MouseEvent | MouseEvent,
    navigation: RowNavigation,
): void {
    if (shouldIgnoreNavigationEvent(event) || event.button !== 1) {
        return;
    }
    event.preventDefault();
    openInBackgroundTab(navigation.href);
}

export function getNavigableElementProps(
    navigation: RowNavigation,
): NavigableElementProps {
    return {
        onClick: (event) => handleNavigableElementClick(event, navigation),
        onAuxClick: (event) =>
            handleNavigableElementAuxClick(event, navigation),
    };
}
